import { useState } from 'react';

export default function CTASection() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setSubmitStatus('success');
      setSubmitMessage('Thank you! Redirecting...');
      setEmail('');
      return;
    }
    setSubmitStatus('loading');
    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d97lua2g3iubr452i560', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
      if (response.ok && parsed?.code === 'OK') {
        setSubmitStatus('success');
        setSubmitMessage('Welcome aboard! Check your inbox soon.');
        setEmail('');
      } else {
        if (typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setSubmitStatus('error');
          setSubmitMessage('Submission failed. Please try again.');
        } else {
          setSubmitStatus('error');
          setSubmitMessage(typeof serverMsg === 'string' ? serverMsg : 'Something went wrong. Please try again.');
        }
      }
    } catch {
      setSubmitStatus('error');
      setSubmitMessage('Network error. Please try again.');
    }
  };

  return (
    <section className="bg-background-50 py-16 md:py-20">
      <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
            Start Your Product Research Journey
          </h2>
          <p className="text-base text-foreground-500 leading-relaxed mb-8">
            Join thousands of smart shoppers who use structured spreadsheet research to discover better products, compare options, and save money.
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-primary-500 text-background-50 text-base font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Explore Kakobuy Sheet
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>

        <div className="relative overflow-hidden rounded-2xl bg-background-100 p-6 md:p-8">
          <h3 className="font-heading text-xl font-semibold text-foreground-800 text-center mb-2">Product Categories at a Glance</h3>
          <p className="text-sm text-foreground-500 text-center mb-6">Browse our complete database across all fourteen research categories</p>
          <div className="flex flex-wrap justify-center gap-3">
            <span className="px-4 py-2 rounded-full bg-background-50 text-sm font-medium text-foreground-700 border border-background-200">Shoes</span>
            <span className="px-4 py-2 rounded-full bg-background-50 text-sm font-medium text-foreground-700 border border-background-200">Hoodies</span>
            <span className="px-4 py-2 rounded-full bg-background-50 text-sm font-medium text-foreground-700 border border-background-200">T-Shirts</span>
            <span className="px-4 py-2 rounded-full bg-background-50 text-sm font-medium text-foreground-700 border border-background-200">Jackets</span>
            <span className="px-4 py-2 rounded-full bg-background-50 text-sm font-medium text-foreground-700 border border-background-200">Pants</span>
            <span className="px-4 py-2 rounded-full bg-background-50 text-sm font-medium text-foreground-700 border border-background-200">Headwear</span>
            <span className="px-4 py-2 rounded-full bg-background-50 text-sm font-medium text-foreground-700 border border-background-200">Sets</span>
            <span className="px-4 py-2 rounded-full bg-background-50 text-sm font-medium text-foreground-700 border border-background-200">Underwear</span>
            <span className="px-4 py-2 rounded-full bg-background-50 text-sm font-medium text-foreground-700 border border-background-200">Jersey</span>
            <span className="px-4 py-2 rounded-full bg-background-50 text-sm font-medium text-foreground-700 border border-background-200">Accessories</span>
            <span className="px-4 py-2 rounded-full bg-background-50 text-sm font-medium text-foreground-700 border border-background-200">Bags</span>
            <span className="px-4 py-2 rounded-full bg-background-50 text-sm font-medium text-foreground-700 border border-background-200">Belts</span>
            <span className="px-4 py-2 rounded-full bg-background-50 text-sm font-medium text-foreground-700 border border-background-200">Perfumes</span>
            <span className="px-4 py-2 rounded-full bg-background-50 text-sm font-medium text-foreground-700 border border-background-200">Other Stuff</span>
          </div>
        </div>

        <div className="mt-12 bg-accent-50 rounded-2xl p-8 md:p-10 text-center max-w-xl mx-auto">
          <h4 className="font-heading text-lg font-semibold text-foreground-800 mb-2">Stay Updated</h4>
          <p className="text-sm text-foreground-600 mb-5">Get the latest spreadsheet research alerts and trending product discoveries.</p>
          <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="flex-1 bg-background-50 border border-background-200 rounded-xl px-4 py-3 text-sm text-foreground-800 placeholder-foreground-400 outline-none focus:border-accent-400 transition-colors"
            />
            <input
              type="text"
              name="company_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="newsletter-honeypot"
            />
            <button
              type="submit"
              disabled={submitStatus === 'loading'}
              className="px-6 py-3 rounded-xl bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {submitStatus === 'loading' ? 'Sending...' : 'Get Alerts'}
            </button>
          </form>
          {submitStatus === 'success' && (
            <p className="mt-3 text-xs text-accent-600">{submitMessage}</p>
          )}
          {submitStatus === 'error' && (
            <p className="mt-3 text-xs text-red-500">{submitMessage}</p>
          )}
        </div>
      </div>
    </section>
  );
}